import { z } from 'zod';
import chroma from 'chroma-js';

export const BrandSchema = z.object({
  groupId: z.string().optional().nullable(),
  groupName: z.string().optional().nullable(),
  //
  logoId: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  logoName: z.string().optional().nullable(),
  logoSize: z.number().optional().nullable(),

  title: z.string().optional(),
  mode: z.enum(['dark', 'light']).optional(),
  // Colors
  backgroundColor: z.string().optional(),
  primaryColor: z.string().optional(),
  accentColor: z.string().optional(),
  strokeColor: z.string().optional(),
  fontColor: z.string().optional(),
  fontFamily: z.string().optional(),
  // landing page = lp
  lpTaglineTopRow: z.string(),
  lpTaglineBottomRow: z.string(),
  lpSubtext: z.string(),
  lpBackgroundImage: z.enum([
    'abstract_1',
    'abstract_2',
    'abstract_3',
    'abstract_4',
    'abstract_5',
  ]),
  lpAccentImage: z.enum([
    'vortex',
    'abstractGlobe',
    'hexagon',
    'dotPulse',
    'prism',
    'custom',
  ]),
  lpAccentImageCustomId: z.string().optional().nullable(),
  lpAccentImageCustom: z.string().optional().nullable(),
  lpAccentImageCustomName: z.string().optional().nullable(),
  lpAccentImageCustomSize: z.number().optional().nullable(),
});

export type BrandType = z.infer<typeof BrandSchema>;

export const WhiteLabelAssetSchema = z.object({
  id: z.string(),
  company_slug: z.string(),
  asset_name: z.string(),
  asset_type: z.string(),
  file_extension: z.string(),
  s3_path: z.string(),
  s3_url: z.string(),
  file_size: z.number().nullable().optional(),
  mime_type: z.string().nullable().optional(),
  group_id: z.string(),
  user_id: z.string(),
  created_at: z.string(),
  is_active: z.boolean(),
  url: z.string(),
});

export const WhiteLabelAssetMinimalSchema = WhiteLabelAssetSchema.pick({
  id: true,
  file_size: true,
  url: true,
  asset_name: true,
});

export type WhiteLabelAssetMinimal = z.infer<
  typeof WhiteLabelAssetMinimalSchema
>;
export type WhiteLabelAsset = z.infer<typeof WhiteLabelAssetSchema>;

export const BrandFormSchema = z
  .object({
    logo: WhiteLabelAssetMinimalSchema.optional().nullable(),
    title: z.string().optional(),
    mode: z.enum(['dark', 'light']).optional(),
    // Colors
    backgroundColor: z
      .string()
      .optional()
      .refine(
        (color) => color === undefined || color === '' || chroma.valid(color),
        {
          message: 'Invalid hex code',
        },
      ),
    primaryColor: z
      .string()
      .optional()
      .refine(
        (color) => color === undefined || color === '' || chroma.valid(color),
        {
          message: 'Invalid hex code',
        },
      ),
    accentColor: z
      .string()
      .optional()
      .refine(
        (color) => color === undefined || color === '' || chroma.valid(color),
        {
          message: 'Invalid hex code',
        },
      ),
    strokeColor: z
      .string()
      .optional()
      .refine(
        (color) => color === undefined || color === '' || chroma.valid(color),
        {
          message: 'Invalid hex code',
        },
      ),
    fontColor: z
      .string()
      .optional()
      .refine(
        (color) => color === undefined || color === '' || chroma.valid(color),
        {
          message: 'Invalid hex code',
        },
      ),
    fontFamily: z.string().optional(),
    // landing page = lp
    lpTaglineTopRow: z
      .string()
      .max(40, 'Tagline top row cannot exceed 40 characters'),
    lpTaglineBottomRow: z
      .string()
      .max(30, 'Tagline bottom row cannot exceed 30 characters'),
    lpSubtext: z.string().max(70, 'Subtext cannot exceed 70 characters'),
    lpBackgroundImage: z.enum([
      'abstract_1',
      'abstract_2',
      'abstract_3',
      'abstract_4',
      'abstract_5',
    ]),
    lpAccentImage: z.enum([
      'vortex',
      'abstractGlobe',
      'hexagon',
      'dotPulse',
      'prism',
      'custom',
    ]),
    lpAccentImageCustom: WhiteLabelAssetMinimalSchema.optional().nullable(),
  })
  .refine(
    (data) => {
      if (data.lpAccentImage === 'custom') {
        return data.lpAccentImageCustom?.url;
      }
      return true;
    },
    {
      path: ['lpAccentImageCustom'],
      message:
        'Accent image upload is required when accent image is set to `Upload to my own`',
    },
  );

export type BrandFormType = z.infer<typeof BrandFormSchema>;

export interface BrandModel {
  id?: string;
  company_name: string;
  company_slug: string;
  config_data: BrandType;
}
